import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  type ShareUrlUpdatedColorRole,
  useTrackBrandContextSelected,
  useTrackCoverImageAdded,
  useTrackCustomLogoAdded,
  useTrackShareLinkFontUpdated,
} from '@air/analytics';
import { type Clip } from '@air/api/types';
import { type SegmentedControlOption } from '@air/component-segmented-control';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import {
  type ShareCustomColors,
  type ShareDesignColorRole,
} from '~/components/ShareModal/components/ShareDesignColorRow';
import { type ShareDesignImageOption } from '~/components/ShareModal/components/ShareDesignImageButton';
import {
  type BrandColorEntry,
  type BrandContextHexByRole,
  type BrandFontEntry,
  DEFAULT_BUTTON_BG_HEX,
  DEFAULT_COLOR_MODE_OPTION,
  DEFAULT_HEADER_BG_HEX,
  DEFAULT_PAGE_BG_HEX,
  DEFAULT_PAGE_WIDTH_OPTION,
  DEFAULT_THUMBNAIL_RADIUS_OPTION,
  mapBrandColorEntryToColorRef,
  mapBrandColors,
  mapBrandColorsToRoles,
  mapBrandFonts,
  mapBrandImages,
  resolveColorRowProps,
  resolveRoleHex,
  SHARE_LINK_DESIGN_COLOR_MODE_OPTIONS,
  SHARE_LINK_DESIGN_PAGE_WIDTH_OPTIONS,
  SHARE_LINK_DESIGN_THUMBNAIL_RADIUS_OPTIONS,
  type TransientColors,
} from '~/components/ShareModal/shareLinkDesignResolution';
import { type CustomFontsInput, type CustomLogoInput } from '~/components/ShareModal/types/presentationSettingsInputs';
import {
  type CreateShowcaseDesignPreviewColorMode,
  type CreateShowcaseDesignPreviewCoverWidth,
  type CreateShowcaseDesignPreviewPageWidth,
  type CreateShowcaseDesignPreviewThumbnailRadius,
} from '~/components/ShowcaseModal/CreateShowcaseDesignPreview';
import {
  buildShowcaseDesignColorRoles,
  COVER_FADE_DEFAULT,
  COVER_WIDTH_DEFAULT,
  DEFAULT_COVER_WIDTH_OPTION,
  SHOWCASE_DESIGN_COVER_WIDTH_OPTIONS,
} from '~/components/ShowcaseModal/showcaseDesignColorResolution';
import {
  type CoverImageInput,
  type ShowcaseCoverImage,
  type ShowcaseCustomFonts,
  type ShowcaseCustomLogo,
} from '~/components/ShowcaseModal/types';
import { COLOR_MODE_DEFAULT } from '~/components/v2/components/shared/share-link-design/shareLinkColorMode';
import { PAGE_WIDTH_DEFAULT } from '~/components/v2/components/shared/share-link-design/shareLinkPageWidth';
import { THUMBNAIL_RADIUS_DEFAULT } from '~/components/v2/components/shared/share-link-design/shareLinkThumbnailRadius';
import { usePrivateContextSourcesGet } from '~/components/v2/hooks/queries/usePrivateContextSourcesGet';
import { useShareSettings } from '~/hooks/useShareSettings';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { isDevOrTestStage } from '~/swr-hooks/utils';

/**
 * Create color roles reuse the shared role config but supply create-namespaced testIds, so the
 * create and editor Design tabs stay independently testable.
 */
const CREATE_SHOWCASE_DESIGN_COLOR_ROLES: ShareDesignColorRole[] = buildShowcaseDesignColorRoles({
  buttonBgColor: 'CREATE_COLLECTION_LINK_DESIGN_COLOR_SWATCH_BUTTONS_AND_HEADLINES',
  headerBgColor: 'CREATE_COLLECTION_LINK_DESIGN_COLOR_SWATCH_HEADER_BACKGROUND',
  pageBgColor: 'CREATE_COLLECTION_LINK_DESIGN_COLOR_SWATCH_PAGE_BACKGROUND',
});

/**
 * Commit sinks may return a write's promise (hence the sinks' `unknown` return — a `void | Promise`
 * union trips Biome's noConfusingVoidType). Normalize both shapes so a rejected write always lands
 * in `onCommitError` instead of escaping as an unhandled rejection. The create modal's draft setters
 * return `undefined`, which resolves and never hits the catch.
 */
const catchCommit = (commitResult: unknown, onCommitError: () => void): void => {
  Promise.resolve(commitResult).catch(onCommitError);
};

export type CreateShowcaseDesignTabCommitAnalytics = {
  updated_setting: 'custom_colors';
  color_role: ShareUrlUpdatedColorRole;
  color_value: string | null;
};

/** The injected create-mode draft data + commit sinks the modal supplies to the Design tab. */
export type CreateShowcaseDesignProviderProps = {
  /** Current draft colors. */
  customColors: ShareCustomColors | undefined;
  /**
   * The workspace's stored share-link defaults (FOUND-133), seeding the initial value of the
   * preview-only layout/theme controls (page theme, corner rounding, page width) — those controls
   * are never sent at create, and the server applies these same defaults, so without the seed the
   * preview shows the system look while the created showcase comes out with the workspace's. Read
   * once at mount: the create modal renders nothing until the defaults query settles.
   */
  defaultPresentationSettings:
    | {
        colorMode?: CreateShowcaseDesignPreviewColorMode;
        thumbnailRadius?: CreateShowcaseDesignPreviewThumbnailRadius;
        pageWidth?: CreateShowcaseDesignPreviewPageWidth;
      }
    | undefined;
  /**
   * Current draft cover image, if any. The tab only reads `url` via a presence guard, so both the
   * read shape and the write Input shape work.
   */
  coverImage: ShowcaseCoverImage | CoverImageInput | undefined;
  /**
   * Current draft custom logo, if any. The tab only reads `url` (presence guard) and the
   * contextSource ref fields (discriminant guard), so both the read shape and the write Input
   * shape work.
   */
  customLogo: ShowcaseCustomLogo | CustomLogoInput | undefined;
  /**
   * Current draft custom fonts, if any. The tab only reads `fontFamily`/`variant` via presence
   * guards, so both the read shape and the write Input shape work.
   */
  customFonts: ShowcaseCustomFonts | CustomFontsInput | undefined;
  /** Preview title (cosmetic). */
  showcaseTitle: string;
  isLoading: boolean;
  /**
   * Commit sink for the draft colors. `nextCustomColors` is always the FULL desired object (see
   * handleCommitColor below).
   *
   * All sinks below may return a write's promise; the provider catches rejections itself (toast +
   * transient cleanup), so sinks should return — not swallow — failures. The create modal's draft
   * setters return `void` and never fail.
   */
  onCommitColors: (nextCustomColors: ShareCustomColors, analytics: CreateShowcaseDesignTabCommitAnalytics) => unknown;
  /** Commit sink for the custom logo; `null` clears it. Asset-pick only this pass (see TODO). */
  onCommitCustomLogo: (customLogo: CustomLogoInput | null) => unknown;
  /** Commit sink for the cover image; `null` clears it. Asset-pick only this pass (see TODO). */
  onCommitCoverImage: (coverImage: CoverImageInput | null) => unknown;
  /** Commit sink for the custom fonts; `null` clears it. Built-in (`system`) fonts only this pass. */
  onCommitCustomFonts: (customFonts: CustomFontsInput | null) => unknown;
  /**
   * Bulk colors commit for applying a brand context. Receives the FULL desired `customColors` object
   * each time, mirroring the per-role commit's full-replacement contract (see handleCommitColor).
   */
  onApplyBrandColors: (nextCustomColors: ShareCustomColors) => unknown;
};

export type CreateShowcaseDesignContextValue = {
  /** Cosmetic preview title. */
  showcaseTitle: string;
  /** Current workspace logo URL (preview fallback identity), if any. */
  workspaceLogoSrc: string | undefined;
  /** Current workspace name (preview fallback identity). */
  workspaceName: string;

  /** All color roles the Site design section renders. */
  colorRoles: ShareDesignColorRole[];
  /**
   * Resolves a color row's display props (display hex + custom flag + backing brand value id) for
   * a given role, so the per-role color row components stay thin and don't recompute the
   * provider's resolution chain.
   */
  getColorRowProps: (role: ShareDesignColorRole) => {
    displayHex: string;
    isCustomColor: boolean;
    selectedBrandColorId: string | undefined;
  };
  /** Per-role uncommitted preview hexes (transient, never persisted). */
  transientColors: TransientColors;
  /** Stored colors backing each row's custom/default state. */
  customColors: ShareCustomColors | undefined;
  /** Resolved-from-brand-context hexes per role, for the transient preview before save round-trips. */
  contextHexByRole: BrandContextHexByRole;

  /** Currently selected brand context source id (`undefined` => None). */
  selectedContextSourceId: string | undefined;
  /** Brand-kit image options for the LOGO picker dropdown (empty when no brand context selected). */
  logoBrandOptions: ShareDesignImageOption[];
  /** The brand value id backing the current logo, when the stored logo is a contextSource ref. */
  selectedLogoBrandOptionId: string | undefined;
  /**
   * Whether a custom logo VALUE exists in the draft, independent of whether its preview URL has
   * resolved — "Remove image" must gate on the value, not the preview.
   */
  hasCustomLogo: boolean;
  /** Brand-kit font entries for the font row's dropdown (empty when no brand context selected). */
  brandFonts: BrandFontEntry[];
  /** Brand-kit color entries for the color rows' swatches (empty when no brand context selected). */
  brandColors: BrandColorEntry[];
  /** The brand value id backing the current font, when the stored font is a contextSource ref. */
  selectedBrandFontId: string | undefined;

  /** Resolved hex for the page background (transient ?? stored ?? brand ?? default). */
  pageHex: string;
  /** Resolved hex for the header bar (transient ?? stored ?? brand ?? default). */
  headerHex: string;
  /** Resolved hex for the buttons & headlines accent (transient ?? stored ?? brand ?? default). */
  buttonHex: string;

  /** Resolved logo URL (just-picked transient ?? server-resolved). */
  logoSrc: string | undefined;
  /** Resolved cover URL (just-picked transient ?? server-resolved). */
  coverSrc: string | undefined;
  /** Selected built-in headline font family, if any. */
  fontFamily: string | undefined;
  /** Selected built-in headline font variant, if any. */
  fontVariant: string | undefined;
  /**
   * Uploaded font file backing the current font (brand entry ?? stored resolved read shape), for
   * the preview's `@font-face` registration. Absent for built-in families — they load by name.
   */
  fontFaceUrl: string | undefined;

  /** Preview-only layout/theme control values (never persisted). */
  thumbnailRadius: CreateShowcaseDesignPreviewThumbnailRadius;
  coverFade: boolean;
  colorMode: CreateShowcaseDesignPreviewColorMode;
  coverWidth: CreateShowcaseDesignPreviewCoverWidth;
  pageWidth: CreateShowcaseDesignPreviewPageWidth;

  /** Plan/loading gates the controls read. */
  brandingDisabled: boolean;
  /**
   * Whether the draft is still loading. Exposed raw (not only folded into the derived gates below)
   * because the cover-image controls carry NO plan gate — the cover image is available on every
   * plan — but must still stay disabled until there is a draft to write into.
   */
  isLoading: boolean;
  layoutDisabled: boolean;
  typographyDisabled: boolean;
  brandCustomizationEnabled: boolean;
  customTypographyEnabled: boolean;

  /** Segmented-control options (memoized with the disabled gate applied). */
  coverWidthOptions: SegmentedControlOption[];
  pageWidthOptions: SegmentedControlOption[];
  thumbnailRadiusOptions: SegmentedControlOption[];
  colorModeOptions: SegmentedControlOption[];
  /** Currently selected option per segmented control. */
  selectedCoverWidthOption: SegmentedControlOption;
  selectedPageWidthOption: SegmentedControlOption;
  selectedThumbnailRadiusOption: SegmentedControlOption;
  selectedColorModeOption: SegmentedControlOption;

  /** Handlers. */
  handleSelectBrandContext: (nextId: string | undefined) => void;
  handleSelectLogo: (clip: Clip) => void;
  handleClearLogo: () => void;
  handleSelectCover: (clip: Clip) => void;
  handleClearCover: () => void;
  handleClearTransientColor: (role: ShareDesignColorRole) => void;
  handlePreviewChange: (role: ShareDesignColorRole, hex: string) => void;
  handleCommitColor: (role: ShareDesignColorRole, hex: string | null) => void;
  handleCommitBrandColor: (role: ShareDesignColorRole, entry: BrandColorEntry) => void;
  handleResetColor: (role: ShareDesignColorRole) => void;
  handleChangeFont: (next: { fontFamily: string; variant?: string }) => void;
  handleClearFont: () => void;
  handleSelectBrandFont: (brandFont: BrandFontEntry) => void;
  handleSelectCoverWidth: (option: SegmentedControlOption) => void;
  handleSelectPageWidth: (option: SegmentedControlOption) => void;
  handleSelectThumbnailRadius: (option: SegmentedControlOption) => void;
  handleToggleCoverFade: (coverFade: boolean) => void;
  handleSelectColorMode: (option: SegmentedControlOption) => void;
};

const defaultContextValue: CreateShowcaseDesignContextValue = {
  showcaseTitle: '',
  workspaceLogoSrc: undefined,
  workspaceName: '',
  colorRoles: CREATE_SHOWCASE_DESIGN_COLOR_ROLES,
  getColorRowProps: (role) => ({ displayHex: role.defaultHex, isCustomColor: false, selectedBrandColorId: undefined }),
  transientColors: {},
  customColors: undefined,
  contextHexByRole: {},
  selectedContextSourceId: undefined,
  logoBrandOptions: [],
  selectedLogoBrandOptionId: undefined,
  hasCustomLogo: false,
  brandFonts: [],
  brandColors: [],
  selectedBrandFontId: undefined,
  pageHex: DEFAULT_PAGE_BG_HEX,
  headerHex: DEFAULT_HEADER_BG_HEX,
  buttonHex: DEFAULT_BUTTON_BG_HEX,
  logoSrc: undefined,
  coverSrc: undefined,
  fontFamily: undefined,
  fontVariant: undefined,
  fontFaceUrl: undefined,
  coverWidth: COVER_WIDTH_DEFAULT,
  pageWidth: PAGE_WIDTH_DEFAULT,
  thumbnailRadius: THUMBNAIL_RADIUS_DEFAULT,
  coverFade: COVER_FADE_DEFAULT,
  colorMode: COLOR_MODE_DEFAULT,
  brandingDisabled: false,
  isLoading: false,
  layoutDisabled: false,
  typographyDisabled: false,
  brandCustomizationEnabled: false,
  customTypographyEnabled: false,
  coverWidthOptions: SHOWCASE_DESIGN_COVER_WIDTH_OPTIONS,
  pageWidthOptions: SHARE_LINK_DESIGN_PAGE_WIDTH_OPTIONS,
  thumbnailRadiusOptions: SHARE_LINK_DESIGN_THUMBNAIL_RADIUS_OPTIONS,
  colorModeOptions: SHARE_LINK_DESIGN_COLOR_MODE_OPTIONS,
  // Derived from the value defaults (COVER_WIDTH_DEFAULT/PAGE_WIDTH_DEFAULT/THUMBNAIL_RADIUS_DEFAULT/
  // COLOR_MODE_DEFAULT set above) so the seed's selected options can never drift from what those
  // values actually mean.
  selectedCoverWidthOption: DEFAULT_COVER_WIDTH_OPTION,
  selectedPageWidthOption: DEFAULT_PAGE_WIDTH_OPTION,
  selectedThumbnailRadiusOption: DEFAULT_THUMBNAIL_RADIUS_OPTION,
  selectedColorModeOption: DEFAULT_COLOR_MODE_OPTION,
  handleSelectBrandContext: () => {},
  handleSelectLogo: () => {},
  handleClearLogo: () => {},
  handleSelectCover: () => {},
  handleClearCover: () => {},
  handleClearTransientColor: () => {},
  handlePreviewChange: () => {},
  handleCommitColor: () => {},
  handleCommitBrandColor: () => {},
  handleResetColor: () => {},
  handleChangeFont: () => {},
  handleClearFont: () => {},
  handleSelectBrandFont: () => {},
  handleSelectCoverWidth: () => {},
  handleSelectPageWidth: () => {},
  handleSelectThumbnailRadius: () => {},
  handleToggleCoverFade: () => {},
  handleSelectColorMode: () => {},
};

export const CreateShowcaseDesignContext = createContext<CreateShowcaseDesignContextValue>(defaultContextValue);

export const useCreateShowcaseDesign = () => {
  const ctx = useContext(CreateShowcaseDesignContext);

  if (ctx === defaultContextValue) {
    const error = new Error('useCreateShowcaseDesign must be used within CreateShowcaseDesignProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Share collection design context not found',
    });
  }

  return ctx;
};

export const CreateShowcaseDesignProvider = ({
  children,
  customColors,
  coverImage,
  customLogo,
  customFonts,
  defaultPresentationSettings,
  showcaseTitle,
  isLoading,
  onCommitColors,
  onCommitCustomLogo,
  onCommitCoverImage,
  onCommitCustomFonts,
  onApplyBrandColors,
}: CreateShowcaseDesignProviderProps & { children: ReactNode }) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { designSettingsEnabled, brandCustomizationEnabled, customTypographyEnabled } = useShareSettings();
  const { showToast } = useToasts();
  /*
   * Branding events fire when the author commits the change in the control, matching the board
   * modal and the fullscreen editor — the create flow's own PATCH lands later, on submit.
   *
   * `share_url_updated` fires from NEITHER the colors nor the layout controls here, and that is
   * deliberate: this surface CREATES a link (its creation reports as `generated_share_url`), so
   * there is no existing link whose setting was "updated". Page width, corner rounding, and page
   * theme are preview-only on this surface anyway — they have no commit sink at all.
   */
  const { trackBrandContextSelected } = useTrackBrandContextSelected();
  const { trackCoverImageAdded } = useTrackCoverImageAdded();
  const { trackCustomLogoAdded } = useTrackCustomLogoAdded();
  const { trackShareLinkFontUpdated } = useTrackShareLinkFontUpdated();
  const [transientColors, setTransientColors] = useState<TransientColors>({});
  /**
   * A freshly picked asset has no server-resolved `url` yet (the backend resolves it on save), so
   * the just-picked thumbnail URL is tracked locally to drive the preview until the stored value
   * comes back with a `url`. Cleared when the image is removed.
   */
  const [transientLogoSrc, setTransientLogoSrc] = useState<string | undefined>(undefined);
  const [transientCoverSrc, setTransientCoverSrc] = useState<string | undefined>(undefined);

  /*
   * Defensive rejection handling for the commit sinks (see catchCommit): the create modal's draft
   * setters are sync and never reject, so these handlers are inert today — they exist so a future
   * promise-returning sink can't escape as an unhandled rejection. The provider's job on failure is
   * the user-visible toast plus clearing any transient preview the failed write was backing.
   */
  const handleCommitError = useCallback(() => {
    showToast('Failed to save design settings. Please try again.');
  }, [showToast]);

  const handleLogoCommitError = useCallback(() => {
    // Drop the just-picked preview so the tab doesn't keep showing a logo that never saved (the
    // cache rollback restores the stored value underneath).
    setTransientLogoSrc(undefined);
    showToast('Failed to update logo. Please try again.');
  }, [showToast]);

  const handleCoverCommitError = useCallback(() => {
    setTransientCoverSrc(undefined);
    showToast('Failed to update cover image. Please try again.');
  }, [showToast]);

  /*
   * Preview-only layout/theme controls: local state that ONLY feeds the live preview — the create
   * flow doesn't carry any of them in its draft, so none reach the create payload; the fullscreen
   * collection editor is where they're authored on an existing link. Page theme, corner rounding,
   * and page width initialize from the workspace defaults (the server applies those same defaults
   * at create, so the preview's starting point matches the link that will actually come out);
   * cover width/fade have no workspace-defaults authoring surface to seed from.
   * @TODO(COR-1951): wire coverWidth/pageWidth into the create payload — their
   * presentationSettings fields exist on the wire now.
   */
  const [coverWidth, setCoverWidth] = useState<CreateShowcaseDesignPreviewCoverWidth>(COVER_WIDTH_DEFAULT);
  const [pageWidth, setPageWidth] = useState<CreateShowcaseDesignPreviewPageWidth>(
    defaultPresentationSettings?.pageWidth ?? PAGE_WIDTH_DEFAULT,
  );
  const [thumbnailRadius, setThumbnailRadius] = useState<CreateShowcaseDesignPreviewThumbnailRadius>(
    defaultPresentationSettings?.thumbnailRadius ?? THUMBNAIL_RADIUS_DEFAULT,
  );
  const [coverFade, setCoverFade] = useState<boolean>(COVER_FADE_DEFAULT);
  const [colorMode, setColorMode] = useState<CreateShowcaseDesignPreviewColorMode>(
    defaultPresentationSettings?.colorMode ?? COLOR_MODE_DEFAULT,
  );

  // Brand context selection is not persisted; it's a transient seeding control (see TODOs below).
  const [selectedContextSourceId, setSelectedContextSourceId] = useState<string | undefined>(undefined);
  /**
   * Hexes resolved from the selected brand context, keyed by role. Drives the preview/swatches for
   * the moment between applying a contextSource ref and the stored value coming back resolved.
   */
  const [contextHexByRole, setContextHexByRole] = useState<BrandContextHexByRole>({});
  // Guards the auto-map effect so a given selection applies exactly once (not on every color edit).
  const appliedSourceIdRef = useRef<string | undefined>(undefined);
  /**
   * Rehydration guards. The selection itself isn't persisted, but every stored contextSource ref
   * carries its owning kit id, so the selection is derived synchronously below and seeded once.
   * `hasUserPickedBrandContextRef` makes an explicit user pick (including "None") always beat
   * rehydration, and `hasRehydratedBrandContextRef` makes rehydration one-shot.
   */
  const hasUserPickedBrandContextRef = useRef(false);
  const hasRehydratedBrandContextRef = useRef(false);

  // Every stored brand-kit ref carries its owning kit id, so the rehydrated selection is derived
  // synchronously: first contextSource ref found wins (logo, then colors, then the headline font —
  // one kit authors them all, so they agree whenever more than one is stored).
  const storedContextSourceId = useMemo(() => {
    if (customLogo?.type === 'contextSource') {
      return customLogo.contextSourceId;
    }

    const storedColorValues = [
      customColors?.headerBgColor,
      customColors?.buttonBgColor,
      customColors?.headlineBgColor,
      customColors?.pageBgColor,
      customColors?.accentBgColor,
    ];

    for (const colorValue of storedColorValues) {
      if (colorValue?.type === 'contextSource') {
        return colorValue.contextSourceId;
      }
    }

    const storedHeadlineFont = customFonts?.headlineFont;

    // A font-only brand link (no brand logo/colors stored) must still rehydrate its kit selection.
    if (storedHeadlineFont?.type === 'contextSource') {
      return storedHeadlineFont.contextSourceId;
    }

    return undefined;
  }, [customColors, customFonts, customLogo]);

  useEffect(() => {
    // Rehydrate the brand-context selection from the stored refs at most once, and never over an
    // explicit user pick (including picking "None" before rehydration runs).
    if (
      !storedContextSourceId ||
      hasRehydratedBrandContextRef.current ||
      hasUserPickedBrandContextRef.current ||
      selectedContextSourceId
    ) {
      return;
    }

    hasRehydratedBrandContextRef.current = true;

    // CRITICAL: pre-seed the applied guard BEFORE selecting. The brand-color auto-apply effect
    // below fires `onApplyBrandColors` (a commit) whenever a NEWLY selected source's fields load;
    // seeding `appliedSourceIdRef` first makes it treat the rehydrated source as already applied,
    // so rehydration stays inert — it must never produce a PATCH on load.
    appliedSourceIdRef.current = storedContextSourceId;
    setSelectedContextSourceId(storedContextSourceId);
  }, [storedContextSourceId, selectedContextSourceId]);

  const { data: selectedSource } = usePrivateContextSourcesGet(
    { workspaceId: currentWorkspace?.id, contextSourceId: selectedContextSourceId },
    { enabled: !!selectedContextSourceId },
  );

  // The fetched source can outlive the selection (react-query keeps the last data once the query is
  // disabled), so brand values gate on the id matching the current selection — same guard as the
  // auto-map effect below.
  const activeBrandSource =
    selectedSource && selectedSource.id === selectedContextSourceId ? selectedSource : undefined;

  const logoBrandOptions = useMemo<ShareDesignImageOption[]>(() => {
    if (!activeBrandSource) {
      return [];
    }

    return mapBrandImages(activeBrandSource).map((entry) => ({
      id: entry.id,
      fieldName: entry.fieldName,
      imageSrc: entry.url,
      label: entry.label,
      onSelect: () => {
        // Same transient-preview pattern as handleSelectLogo: the brand value's URL drives the
        // preview until the stored contextSource ref round-trips with its server-resolved `url`.
        setTransientLogoSrc(entry.url);
        trackCustomLogoAdded({ from_brand_context: true, share_link_type: 'collection' });
        catchCommit(
          onCommitCustomLogo({
            type: 'contextSource',
            contextSourceId: entry.contextSourceId,
            contextSourceFieldId: entry.contextSourceFieldId,
            contextSourceFieldValueId: entry.id,
          }),
          handleLogoCommitError,
        );
      },
    }));
  }, [activeBrandSource, handleLogoCommitError, onCommitCustomLogo, trackCustomLogoAdded]);

  const brandFonts = useMemo<BrandFontEntry[]>(
    () => (activeBrandSource ? mapBrandFonts(activeBrandSource) : []),
    [activeBrandSource],
  );

  const brandColors = useMemo<BrandColorEntry[]>(
    () => (activeBrandSource ? mapBrandColors(activeBrandSource) : []),
    [activeBrandSource],
  );

  const handleSelectLogo = useCallback(
    (clip: Clip) => {
      setTransientLogoSrc(clip.assets.image);
      trackCustomLogoAdded({ from_brand_context: false, share_link_type: 'collection' });
      // TODO(COR-1951 later pass): support `upload` (base64) logo sources. `contextSource` logos
      // are handled by logoBrandOptions above.
      catchCommit(
        onCommitCustomLogo({ type: 'asset', assetId: clip.assetId, versionId: clip.id }),
        handleLogoCommitError,
      );
    },
    [handleLogoCommitError, onCommitCustomLogo, trackCustomLogoAdded],
  );

  const handleClearLogo = useCallback(() => {
    setTransientLogoSrc(undefined);
    catchCommit(onCommitCustomLogo(null), handleLogoCommitError);
  }, [handleLogoCommitError, onCommitCustomLogo]);

  const handleSelectCover = useCallback(
    (clip: Clip) => {
      setTransientCoverSrc(clip.assets.image);
      trackCoverImageAdded({ type: 'collection' });
      // TODO(COR-1951 later pass): reposition/crop UX. Backend accepts a region; default to top-anchored.
      catchCommit(
        onCommitCoverImage({ assetId: clip.assetId, versionId: clip.id, region: { top: 0 } }),
        handleCoverCommitError,
      );
    },
    [handleCoverCommitError, onCommitCoverImage, trackCoverImageAdded],
  );

  const handleClearCover = useCallback(() => {
    setTransientCoverSrc(undefined);
    catchCommit(onCommitCoverImage(null), handleCoverCommitError);
  }, [handleCoverCommitError, onCommitCoverImage]);

  const handleClearTransientColor = useCallback((role: ShareDesignColorRole) => {
    setTransientColors((previous) => {
      const next = { ...previous };
      delete next[role.readKey];
      return next;
    });
  }, []);

  const handlePreviewChange = useCallback((role: ShareDesignColorRole, hex: string) => {
    setTransientColors((previous) => ({ ...previous, [role.readKey]: hex }));
  }, []);

  const handleCommitColor = useCallback(
    (role: ShareDesignColorRole, hex: string | null) => {
      /**
       * The update path wholesale-replaces `presentationSettings.customColors`, so the FULL desired
       * object is sent every time: stored roles this UI doesn't author (e.g. accentBgColor) are
       * carried over, and a reset role is cleared by omitting its key.
       */
      const nextCustomColors: ShareCustomColors = { ...customColors };

      for (const writeKey of role.writeKeys) {
        if (hex === null) {
          delete nextCustomColors[writeKey];
        } else {
          nextCustomColors[writeKey] = { type: 'hex', hex };
        }
      }

      handleClearTransientColor(role);

      catchCommit(
        onCommitColors(nextCustomColors, {
          updated_setting: 'custom_colors',
          color_role: role.analyticsRole,
          color_value: hex,
        }),
        handleCommitError,
      );
    },
    [customColors, handleClearTransientColor, handleCommitError, onCommitColors],
  );

  const handleCommitBrandColor = useCallback(
    (role: ShareDesignColorRole, entry: BrandColorEntry) => {
      // Full-replacement customColors, same contract as handleCommitColor above. The picked entry
      // commits as a contextSource REFERENCE on EVERY writeKey of the role (the buttons row
      // authors buttonBgColor + headlineBgColor with one ref, matching the auto-map's mirroring) —
      // the stored ref is what rehydrates the kit selection and swatch label on reload; a plain
      // hex would leave nothing to rehydrate from. The ref's `hex` previews instantly via the
      // draft; the update normalizer strips it on the wire.
      const nextCustomColors: ShareCustomColors = { ...customColors };
      const brandColorRef = mapBrandColorEntryToColorRef(entry);

      for (const writeKey of role.writeKeys) {
        nextCustomColors[writeKey] = brandColorRef;
      }

      handleClearTransientColor(role);

      catchCommit(
        onCommitColors(nextCustomColors, {
          updated_setting: 'custom_colors',
          color_role: role.analyticsRole,
          color_value: entry.hex,
        }),
        handleCommitError,
      );
    },
    [customColors, handleClearTransientColor, handleCommitError, onCommitColors],
  );

  const handleResetColor = useCallback(
    (role: ShareDesignColorRole) => handleCommitColor(role, null),
    [handleCommitColor],
  );

  const handleSelectBrandContext = useCallback(
    (nextId: string | undefined) => {
      // An explicit pick (either direction) permanently wins over any in-flight rehydration.
      hasUserPickedBrandContextRef.current = true;

      if (!nextId) {
        // Clearing the brand context resets the auto-mapped roles back to default by omitting their
        // keys, while preserving roles the map never touches (e.g. accentBgColor). The mapping
        // itself runs async, so there's no source to wait on here.
        setSelectedContextSourceId(undefined);
        setContextHexByRole({});
        appliedSourceIdRef.current = undefined;

        const nextCustomColors: ShareCustomColors = { ...customColors };
        delete nextCustomColors.headerBgColor;
        delete nextCustomColors.buttonBgColor;
        delete nextCustomColors.headlineBgColor;
        delete nextCustomColors.pageBgColor;

        catchCommit(onApplyBrandColors(nextCustomColors), handleCommitError);
        return;
      }

      // The actual color mapping happens in the effect below, once the source's fields load.
      setSelectedContextSourceId(nextId);
    },
    [customColors, handleCommitError, onApplyBrandColors],
  );

  useEffect(() => {
    // Apply once per selection: wait for the fetched source to match the selected id, and skip if
    // we've already applied it (so manual color edits afterward don't re-trigger the brand apply).
    if (
      !selectedSource ||
      selectedSource.id !== selectedContextSourceId ||
      appliedSourceIdRef.current === selectedSource.id
    ) {
      return;
    }

    appliedSourceIdRef.current = selectedSource.id;

    const { nextRoles, contextHexByRole: hexByRole } = mapBrandColorsToRoles(selectedSource);

    setContextHexByRole(hexByRole);

    // Spread current colors so roles the map doesn't match (and untouched roles like accentBgColor)
    // are preserved, then overlay the matched contextSource refs.
    const nextCustomColors: ShareCustomColors = { ...customColors, ...nextRoles };

    // Tracked here rather than in the picker handler because the kit's NAME only exists once the
    // source has been fetched, and this apply is what actually changes the design.
    trackBrandContextSelected({ brand_context_name: selectedSource.name, share_link_type: 'collection' });

    catchCommit(onApplyBrandColors(nextCustomColors), handleCommitError);
  }, [
    selectedSource,
    selectedContextSourceId,
    customColors,
    handleCommitError,
    onApplyBrandColors,
    trackBrandContextSelected,
  ]);

  const handleChangeFont = useCallback(
    (next: { fontFamily: string; variant?: string }) => {
      trackShareLinkFontUpdated({ font: next.fontFamily, from_brand_context: false });

      // Built-in fonts only; brand-kit (`contextSource`) fonts commit via handleSelectBrandFont
      // below, and asset-font upload remains deferred (see row TODO).
      catchCommit(
        onCommitCustomFonts({
          headlineFont: {
            type: 'system',
            fontFamily: next.fontFamily,
            ...(next.variant ? { variant: next.variant } : {}),
          },
        }),
        handleCommitError,
      );
    },
    [handleCommitError, onCommitCustomFonts, trackShareLinkFontUpdated],
  );

  const handleClearFont = useCallback(() => {
    catchCommit(onCommitCustomFonts(null), handleCommitError);
  }, [handleCommitError, onCommitCustomFonts]);

  const handleSelectBrandFont = useCallback(
    (brandFont: BrandFontEntry) => {
      trackShareLinkFontUpdated({ font: brandFont.family, from_brand_context: true });

      // A pure brand-kit ref — the server resolves fontFamily/url on read. The display family comes
      // from the brand entry lookup below until then.
      catchCommit(
        onCommitCustomFonts({
          headlineFont: {
            type: 'contextSource',
            contextSourceId: brandFont.contextSourceId,
            contextSourceFieldId: brandFont.contextSourceFieldId,
            contextSourceFieldValueId: brandFont.id,
          },
        }),
        handleCommitError,
      );
    },
    [handleCommitError, onCommitCustomFonts, trackShareLinkFontUpdated],
  );

  /*
   * Layout/theme control handlers. Each narrows the segmented option's `string` value back to its
   * literal union via equality checks (no type assertions). All of them update LOCAL preview state
   * only — no commit sink, so nothing reaches a payload (see the preview-only comment above).
   */
  // Two plan gates, two meanings: layout (page width, corner rounding) is authorable on any paid
  // plan, while the brand elements (logo, brand kit, colors, page theme) need Business+. The cover
  // reads NEITHER — the whole cover feature is available on every plan.
  const layoutDisabled = !designSettingsEnabled || isLoading;
  const brandingDisabled = !brandCustomizationEnabled || isLoading;

  const handleSelectCoverWidth = useCallback((option: SegmentedControlOption) => {
    if (option.value === 'inset' || option.value === 'fullWidth' || option.value === 'immersive') {
      setCoverWidth(option.value);
    }
  }, []);

  const handleSelectPageWidth = useCallback((option: SegmentedControlOption) => {
    if (option.value === 'narrow' || option.value === 'standard' || option.value === 'wide') {
      setPageWidth(option.value);
    }
  }, []);

  const handleSelectThumbnailRadius = useCallback((option: SegmentedControlOption) => {
    if (option.value === 'standard' || option.value === 'subtle' || option.value === 'square') {
      setThumbnailRadius(option.value);
    }
  }, []);

  // Boolean twin of handleSelectThumbnailRadius (the toggle already hands us the typed next value,
  // so there's no string union to narrow).
  const handleToggleCoverFade = useCallback((nextCoverFade: boolean) => {
    setCoverFade(nextCoverFade);
  }, []);

  // The narrowing deliberately excludes the wire union's 'toggle' member: the authoring options only
  // offer Light/Dark (a viewer-facing theme toggle is a named follow-up).
  const handleSelectColorMode = useCallback((option: SegmentedControlOption) => {
    if (option.value === 'light' || option.value === 'dark') {
      setColorMode(option.value);
    }
  }, []);

  // Alone among the four segmented lists, cover width keeps only the HONEST half of `layoutDisabled`
  // — the whole cover feature (image, width, fade) is available on every plan, so the plan check
  // goes and `isLoading` stays, leaving the row inert while the modal is still booting like every
  // control beside it. The two layout lists below keep the full layout gate, and page theme keeps
  // the brand one.
  const coverWidthOptions = useMemo(
    () => SHOWCASE_DESIGN_COVER_WIDTH_OPTIONS.map((option) => ({ ...option, disabled: isLoading })),
    [isLoading],
  );
  const pageWidthOptions = useMemo(
    () => SHARE_LINK_DESIGN_PAGE_WIDTH_OPTIONS.map((option) => ({ ...option, disabled: layoutDisabled })),
    [layoutDisabled],
  );
  const thumbnailRadiusOptions = useMemo(
    () => SHARE_LINK_DESIGN_THUMBNAIL_RADIUS_OPTIONS.map((option) => ({ ...option, disabled: layoutDisabled })),
    [layoutDisabled],
  );
  const colorModeOptions = useMemo(
    // Page theme is a brand element, so it rides the brand gate rather than the layout gate its
    // option-list siblings above share.
    () => SHARE_LINK_DESIGN_COLOR_MODE_OPTIONS.map((option) => ({ ...option, disabled: brandingDisabled })),
    [brandingDisabled],
  );

  const selectedCoverWidthOption =
    coverWidthOptions.find((option) => option.value === coverWidth) ?? coverWidthOptions[0];
  const selectedPageWidthOption = pageWidthOptions.find((option) => option.value === pageWidth) ?? pageWidthOptions[0];
  const selectedThumbnailRadiusOption =
    thumbnailRadiusOptions.find((option) => option.value === thumbnailRadius) ?? thumbnailRadiusOptions[0];
  // The `?? [0]` arm is reachable here: a stored 'toggle' (wire union member with no authoring
  // option) degrades to showing Light selected rather than no selection.
  const selectedColorModeOption = colorModeOptions.find((option) => option.value === colorMode) ?? colorModeOptions[0];

  const headerHex = resolveRoleHex(
    'headerBgColor',
    transientColors,
    customColors,
    contextHexByRole,
    DEFAULT_HEADER_BG_HEX,
  );
  const buttonHex = resolveRoleHex(
    'buttonBgColor',
    transientColors,
    customColors,
    contextHexByRole,
    DEFAULT_BUTTON_BG_HEX,
  );
  const pageHex = resolveRoleHex('pageBgColor', transientColors, customColors, contextHexByRole, DEFAULT_PAGE_BG_HEX);

  // Just-picked transient URL wins until the stored value comes back resolved with its own `url`.
  // The stored value is a write-capable union, so `url` (Output-only) is read via a presence guard.
  const logoSrc = transientLogoSrc ?? (customLogo && 'url' in customLogo ? customLogo.url : undefined);
  const coverSrc = transientCoverSrc ?? (coverImage && 'url' in coverImage ? coverImage.url : undefined);
  // Display-only twin of selectedBrandFontId below: lets the logo picker show the brand entry's
  // label (and mark it selected) when the stored logo is a brand-kit ref.
  const selectedLogoBrandOptionId =
    customLogo?.type === 'contextSource' ? customLogo.contextSourceFieldValueId : undefined;
  // "Remove image" (and the dropdown itself) must gate on a VALUE existing in the draft, not on a
  // resolved preview URL — a just-committed brand ref can be stored with no `url` yet.
  const hasCustomLogo = !!customLogo;

  // The stored headlineFont is a write-capable union; `fontFamily`/`variant` exist on system/asset
  // and on the resolved contextSource read shape, so they're read via presence guards.
  const headlineFont = customFonts?.headlineFont;
  const selectedBrandFontId =
    headlineFont?.type === 'contextSource' ? headlineFont.contextSourceFieldValueId : undefined;
  const storedFontFamily = headlineFont && 'fontFamily' in headlineFont ? headlineFont.fontFamily : undefined;
  // A just-committed brand font is a pure ref (no fontFamily until the save round-trips resolved),
  // so for contextSource refs the brand entry's family wins, falling back to the resolved read shape
  // (e.g. a saved brand font reopened with no brand context selected this session).
  const selectedBrandFont = selectedBrandFontId
    ? brandFonts.find((brandFont) => brandFont.id === selectedBrandFontId)
    : undefined;
  const fontFamily = selectedBrandFontId ? (selectedBrandFont?.family ?? storedFontFamily) : storedFontFamily;
  const fontVariant = headlineFont && 'variant' in headlineFont ? headlineFont.variant : undefined;
  // Uploaded font files must be registered via @font-face to preview — brand entry (fresh) wins
  // over the stored resolved read shape (e.g. a saved upload reopened without its kit loaded).
  const storedFontFaceUrl =
    headlineFont && headlineFont.type !== 'system' && 'url' in headlineFont ? headlineFont.url : undefined;
  const fontFaceUrl = selectedBrandFont?.fileUrl ?? storedFontFaceUrl;
  const typographyDisabled = !customTypographyEnabled || isLoading;

  const getColorRowProps = useCallback(
    (role: ShareDesignColorRole) => resolveColorRowProps({ role, transientColors, customColors, contextHexByRole }),
    [transientColors, customColors, contextHexByRole],
  );

  const contextValue = useMemo<CreateShowcaseDesignContextValue>(
    () => ({
      showcaseTitle,
      workspaceLogoSrc: currentWorkspace?.image ?? undefined,
      workspaceName: currentWorkspace?.name ?? '',
      colorRoles: CREATE_SHOWCASE_DESIGN_COLOR_ROLES,
      getColorRowProps,
      transientColors,
      customColors,
      contextHexByRole,
      selectedContextSourceId,
      logoBrandOptions,
      selectedLogoBrandOptionId,
      hasCustomLogo,
      brandFonts,
      brandColors,
      selectedBrandFontId,
      pageHex,
      headerHex,
      buttonHex,
      logoSrc,
      coverSrc,
      fontFamily,
      fontVariant,
      fontFaceUrl,
      coverWidth,
      pageWidth,
      thumbnailRadius,
      coverFade,
      colorMode,
      brandingDisabled,
      isLoading,
      layoutDisabled,
      typographyDisabled,
      brandCustomizationEnabled,
      customTypographyEnabled,
      coverWidthOptions,
      pageWidthOptions,
      thumbnailRadiusOptions,
      colorModeOptions,
      selectedCoverWidthOption,
      selectedPageWidthOption,
      selectedThumbnailRadiusOption,
      selectedColorModeOption,
      handleSelectBrandContext,
      handleSelectLogo,
      handleClearLogo,
      handleSelectCover,
      handleClearCover,
      handleClearTransientColor,
      handlePreviewChange,
      handleCommitColor,
      handleCommitBrandColor,
      handleResetColor,
      handleChangeFont,
      handleClearFont,
      handleSelectBrandFont,
      handleSelectCoverWidth,
      handleSelectPageWidth,
      handleSelectThumbnailRadius,
      handleToggleCoverFade,
      handleSelectColorMode,
    }),
    [
      showcaseTitle,
      currentWorkspace?.image,
      currentWorkspace?.name,
      getColorRowProps,
      transientColors,
      customColors,
      contextHexByRole,
      selectedContextSourceId,
      logoBrandOptions,
      selectedLogoBrandOptionId,
      hasCustomLogo,
      brandFonts,
      brandColors,
      selectedBrandFontId,
      pageHex,
      headerHex,
      buttonHex,
      logoSrc,
      coverSrc,
      fontFamily,
      fontVariant,
      fontFaceUrl,
      coverWidth,
      pageWidth,
      thumbnailRadius,
      coverFade,
      colorMode,
      brandingDisabled,
      isLoading,
      layoutDisabled,
      typographyDisabled,
      brandCustomizationEnabled,
      customTypographyEnabled,
      coverWidthOptions,
      pageWidthOptions,
      thumbnailRadiusOptions,
      colorModeOptions,
      selectedCoverWidthOption,
      selectedPageWidthOption,
      selectedThumbnailRadiusOption,
      selectedColorModeOption,
      handleSelectBrandContext,
      handleSelectLogo,
      handleClearLogo,
      handleSelectCover,
      handleClearCover,
      handleClearTransientColor,
      handlePreviewChange,
      handleCommitColor,
      handleCommitBrandColor,
      handleResetColor,
      handleChangeFont,
      handleClearFont,
      handleSelectBrandFont,
      handleSelectCoverWidth,
      handleSelectPageWidth,
      handleSelectThumbnailRadius,
      handleToggleCoverFade,
      handleSelectColorMode,
    ],
  );

  return <CreateShowcaseDesignContext.Provider value={contextValue}>{children}</CreateShowcaseDesignContext.Provider>;
};
